import { useState } from 'react'

import { initRequestParam } from 'constants/constants'

import _ from 'lodash'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { getAPI } from 'utils/getAPI'

import type {
  GetMstTransportTypeTransportTypeGetRequest,
  ResponseResult,
  ResponseUpdate,
  MasterTransportTypeUpdateMstTransportTypeUpdatePostRequest,
  TransportType,
  MstTransportType,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface MstTransportTypeSeq extends MstTransportType{
  id: string
}

export interface MstTransportTypesSeq extends TransportType{
  list: MstTransportTypeSeq[]
}

export const useTransportType = () => {
  const [transportTypes, setTransportTypes] = useState<MstTransportType[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [result, setResult] = useState<ResponseResult>()
  const [transportTypeSearch, setTransportTypeSearch] = useState<MstTransportTypesSeq>(initRequestParam)
  const [transportTypeSearchResult, setTransportTypeSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
    
  const { setCurrentStatuses } = useCurrentStatusStore()

  const fetchTransportTypeSearch = async (searchParam: GetMstTransportTypeTransportTypeGetRequest) => {
    setTransportTypeSearch(initRequestParam)
    setResult(undefined)
    setIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getMstTransportTypeTransportTypeGet(searchParam)

      setTransportTypeSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setTransportTypeSearchResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setTransportTypeSearchResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `輸送種類マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTransportTypeUpdate = async (body: MasterTransportTypeUpdateMstTransportTypeUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setResult(undefined)
    // setTransportTypeUpdateIsLoading(true);
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.masterTransportTypeUpdateMstTransportTypeUpdatePost(body);
      
      setResult(response.result)
      setUpdateResult(response);

      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '入力エラーがあります');
        Object.assign(error, {
            ...response
        });
        throw error;
      }
      return response;
    } catch (error) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
              setUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : `輸送種類マスタの更新でエラーが発生しました。`,
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
          setUpdateResult(errorResponse);
            setResult(errorResponse.result)
            resolve();
        });
        return await Promise.reject(errorResponse);

    } finally {
      setIsUpdating(false);
    }
  };


  return {
    result,
    isLoading,
    isUpdating,
    transportTypes,
    transportTypeSearch,
    fetchTransportTypeSearch,
    transportTypeSearchResult,
    fetchTransportTypeUpdate,
    updateResult,
  }
}